<template>
  <div class="edit-form-list">
    <!-- A4(最大10件)で入力後にL判(2件)へ切り替えた場合など、超過分が黙って欠落しないよう明示する -->
    <div
      v-if="atenaItems.length > maxAtenaItems"
      class="atena-char-count is-over"
    >
      選択中の用紙には宛先{{ maxAtenaItems }}件まで印刷されます。{{
        maxAtenaItems + 1
      }}件目以降は印刷されません
    </div>
    <transition-group name="fade" tag="div">
      <div
        class="edit-form-list-item"
        v-for="(item, index) in atenaItems"
        :key="item.uuid"
        :id="item.uuid"
        :class="{ active: activeHistoryItem == item.uuid }"
        @click="activateHistoryItem(item.uuid)"
      >
        <div class="atena-item">
          <div class="atena-item-content">
            <div class="atena-field">
              <div class="item-name">郵便番号</div>
              <el-input
                placeholder="郵便番号を入力"
                title="郵便番号"
                type="tel"
                v-model="item.postNumber"
                v-on:blur="item.postNumber = cleanUpPostNumber(item.postNumber)"
              ></el-input>
            </div>
            <div class="atena-field">
              <div class="item-name">住所</div>
              <el-input
                placeholder="住所を入力"
                title="住所"
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 3 }"
                v-model="item.address"
              ></el-input>
              <div
                v-if="item.address"
                class="atena-char-count"
                :class="{ 'is-over': isOverLimit(item.address, 'address') }"
              >
                {{ item.address.length }}/{{ charLimits.address }}文字
                <span v-if="isOverLimit(item.address, 'address')"
                  >— 文字数が多いため印刷時にはみ出る可能性があります</span
                >
              </div>
            </div>
            <div class="atena-field">
              <div class="item-name">企業名</div>
              <el-input
                placeholder="企業名を入力"
                title="企業名"
                v-model="item.companyName"
              ></el-input>
              <div
                v-if="item.companyName"
                class="atena-char-count"
                :class="{
                  'is-over': isOverLimit(item.companyName, 'companyName')
                }"
              >
                {{ item.companyName.length }}/{{ charLimits.companyName }}文字
                <span v-if="isOverLimit(item.companyName, 'companyName')"
                  >— 文字数が多いため印刷時にはみ出る可能性があります</span
                >
              </div>
            </div>
            <div class="atena-field">
              <div class="item-name">部署名</div>
              <el-input
                placeholder="部署名を入力"
                title="部署名"
                v-model="item.departmentName"
              ></el-input>
              <div
                v-if="item.departmentName"
                class="atena-char-count"
                :class="{
                  'is-over': isOverLimit(item.departmentName, 'departmentName')
                }"
              >
                {{ item.departmentName.length }}/{{
                  charLimits.departmentName
                }}文字
                <span v-if="isOverLimit(item.departmentName, 'departmentName')"
                  >— 文字数が多いため印刷時にはみ出る可能性があります</span
                >
              </div>
              <div class="atena-note">
                <span
                  >企業名・部署名までの入力の場合「御中」、担当者名まで入力の場合は「様」が自動で挿入されます</span
                >
              </div>
            </div>
            <div class="atena-field">
              <div class="item-name">担当者名</div>
              <el-input
                placeholder="担当者名を入力"
                title="担当者名"
                v-model="item.staffName"
              ></el-input>
              <div
                v-if="item.staffName"
                class="atena-char-count"
                :class="{ 'is-over': isOverLimit(item.staffName, 'staffName') }"
              >
                {{ item.staffName.length }}/{{ charLimits.staffName }}文字
                <span v-if="isOverLimit(item.staffName, 'staffName')"
                  >— 文字数が多いため印刷時にはみ出る可能性があります</span
                >
              </div>
            </div>
          </div>
          <div class="atena-item-actions">
            <el-button icon="el-icon-delete" @click.stop="emit('delete', index)"
              >削除</el-button
            >
            <el-button icon="el-icon-check" @click.stop="deactivateHistoryItem"
              >OK</el-button
            >
          </div>
        </div>
      </div>
    </transition-group>
    <div class="add-list-item" v-if="atenaItems.length < maxAtenaItems">
      <el-button
        icon="el-icon-plus"
        circle
        type="success"
        @click="emit('add')"
      ></el-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type {
  AtenaCharLimits,
  AtenaItem
} from '@/utils/format-edit/atenaFormat'

const props = defineProps<{
  // 親(AtenaEdit)所有の配列。項目フィールドの編集は参照経由、追加・削除は emit で親が行う
  atenaItems: AtenaItem[]
  maxAtenaItems: number
  charLimits: AtenaCharLimits
}>()

const emit = defineEmits<{
  (e: 'add'): void
  (e: 'delete', index: number): void
}>()

const { formLib } = useHostServices()

const activeHistoryItem = ref('')

function activateHistoryItem(id: string) {
  activeHistoryItem.value = id
}

function deactivateHistoryItem() {
  activeHistoryItem.value = ''
}

function isOverLimit(text: string | undefined, field: keyof AtenaCharLimits) {
  if (!text) return false
  return text.length > props.charLimits[field]
}

function cleanUpPostNumber(value: string | undefined) {
  if (!value) return ''
  return String(formLib.zenToHanNumber(value.replace(/-/g, '')))
}

onMounted(() => {
  emitter.on('deactivate-history-item', deactivateHistoryItem)
})

onBeforeUnmount(() => {
  emitter.off('deactivate-history-item', deactivateHistoryItem)
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables';

.atena-item {
  flex: 1 1 0%;
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;

  .atena-item-content {
    padding: 0.5rem;
    position: relative;
    flex: 1;
    min-width: 0;

    .atena-field {
      margin-bottom: 2px;
    }
  }

  .atena-item-actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
    justify-content: flex-end;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
  }
}

.atena-note {
  padding: 0.5rem 1rem;
  font-size: 12px;
  color: $accentcolor;
}

.atena-char-count {
  font-size: 11px;
  color: $gray;
  margin-top: 2px;
  padding-left: 2px;

  &.is-over {
    color: #e74c3c;
    font-weight: bold;
  }
}
</style>
